import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Slider } from '../../components/ui/slider';
import { formatCurrency, formatApiError } from '../../lib/utils';
import { Coins, Check, CreditCard, Loader2, ShieldCheck, X, Zap, Award, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function PaymentOverlay({ overlay, onClose }) {
    if (!overlay.active) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-overlay">
            <div className="bg-[#121620] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-md w-full text-center relative">
                {overlay.status === 'loading' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                            <Loader2 className="w-8 h-8 text-gold animate-spin" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Preparing Payment</h3>
                        <p className="text-muted-foreground text-sm">Opening secure checkout window...</p>
                    </>
                )}
                {overlay.status === 'waiting' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                            <ShieldCheck className="w-8 h-8 text-gold" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Complete Your Payment</h3>
                        <p className="text-muted-foreground text-sm mb-1">
                            A secure payment window has opened.
                        </p>
                        <p className="text-muted-foreground text-sm mb-4">
                            Purchasing <span className="text-gold font-semibold">{overlay.credits} credits</span> for <span className="text-white font-semibold">{formatCurrency(overlay.amount)}</span>
                        </p>
                        <div className="flex items-center justify-center gap-2 text-xs text-white/30 mb-6">
                            <div className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                            Waiting for payment confirmation...
                        </div>
                        <Button variant="ghost" onClick={onClose} className="text-white/50 hover:text-white text-sm" data-testid="cancel-payment-btn">
                            Cancel Payment
                        </Button>
                    </>
                )}
                {overlay.status === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-8 h-8 text-emerald-400" />
                        </div>
                        <h3 className="font-outfit text-xl font-bold text-white mb-2">Payment Successful!</h3>
                        <p className="text-muted-foreground text-sm">
                            <span className="text-gold font-semibold">{overlay.credits} credits</span> have been added to your account.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default function OperatorCredits() {
    const [searchParams] = useSearchParams();
    const [storeData, setStoreData] = useState(null);
    const [currentCredits, setCurrentCredits] = useState(0);
    const [loading, setLoading] = useState(true);
    const [customQty, setCustomQty] = useState(100);
    const [overlay, setOverlay] = useState({ active: false });
    const popupRef = useRef(null);
    const pollRef = useRef(null);

    useEffect(() => {
        fetchData();
        checkPaymentStatus();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const fetchData = async () => {
        try {
            const [packagesRes, dashboardRes] = await Promise.all([
                axios.get(`${API}/api/packages/credits`, { withCredentials: true }),
                axios.get(`${API}/api/operator/dashboard`, { withCredentials: true })
            ]);
            setStoreData(packagesRes.data);
            setCurrentCredits(dashboardRes.data?.operator?.credits || 0);
            if (packagesRes.data?.min_credits) {
                setCustomQty(packagesRes.data.min_credits);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkPaymentStatus = async () => {
        const sessionId = searchParams.get('session_id');
        if (!sessionId) return;

        setOverlay({ active: true, status: 'waiting', credits: '...', amount: 0 });
        let attempts = 0;
        const poll = async () => {
            try {
                const res = await axios.get(`${API}/api/payments/status/${sessionId}`, { withCredentials: true });
                if (res.data.payment_status === 'paid') {
                    setOverlay(prev => ({ ...prev, status: 'success' }));
                    toast.success('Payment successful! Credits added.');
                    fetchData();
                    window.history.replaceState({}, '', '/operator/credits');
                    setTimeout(() => setOverlay({ active: false }), 3000);
                    return;
                }
                if (res.data.status === 'expired') {
                    setOverlay({ active: false });
                    toast.error('Payment session expired.');
                    return;
                }
                attempts++;
                if (attempts < 8) setTimeout(poll, 2000);
                else setOverlay({ active: false });
            } catch (e) {
                console.error('Checkout session poll error:', e);
                setOverlay({ active: false });
            }
        };
        poll();
    };

    const startPurchase = useCallback(async (credits) => {
        const config = storeData;
        if (!config) return;
        const ppc = config.price_per_credit || 1.0;
        let discount = 0;
        for (const pkg of (config.packages || [])) {
            if (credits >= pkg.credits) discount = Math.max(discount, pkg.discount || 0);
        }
        const amount = Math.round(credits * ppc * (1 - discount / 100) * 100) / 100;

        setOverlay({ active: true, status: 'loading', credits, amount });

        try {
            const response = await axios.post(
                `${API}/api/payments/checkout/credits`,
                { credits, origin_url: window.location.origin },
                { withCredentials: true }
            );

            const { url, session_id } = response.data;

            const w = 520, h = 720;
            const left = (window.screen.width - w) / 2;
            const top = (window.screen.height - h) / 2;
            const popup = window.open(url, 'voxoora-checkout', `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
            popupRef.current = popup;

            setOverlay({ active: true, status: 'waiting', credits, amount, session_id });

            pollRef.current = setInterval(async () => {
                if (popup && popup.closed) {
                    clearInterval(pollRef.current);
                    try {
                        const statusRes = await axios.get(`${API}/api/payments/status/${session_id}`, { withCredentials: true });
                        if (statusRes.data.payment_status === 'paid') {
                            setOverlay({ active: true, status: 'success', credits, amount });
                            toast.success('Payment successful! Credits added.');
                            fetchData();
                            setTimeout(() => setOverlay({ active: false }), 3000);
                        } else {
                            setOverlay({ active: false });
                            toast.info('Payment was not completed.');
                        }
                    } catch (e) {
                        console.error('Payment status check failed:', e);
                        setOverlay({ active: false });
                    }
                    return;
                }
                try {
                    const statusRes = await axios.get(`${API}/api/payments/status/${session_id}`, { withCredentials: true });
                    if (statusRes.data.payment_status === 'paid') {
                        clearInterval(pollRef.current);
                        if (popup && !popup.closed) popup.close();
                        setOverlay({ active: true, status: 'success', credits, amount });
                        toast.success('Payment successful! Credits added.');
                        fetchData();
                        setTimeout(() => setOverlay({ active: false }), 3000);
                    }
                } catch (e) {
                    console.error('Payment polling error:', e);
                }
            }, 2500);

            if (!popup || popup.closed) {
                clearInterval(pollRef.current);
                setOverlay({ active: false });
                toast.error('Popup was blocked. Please allow popups for this site.');
            }
        } catch (error) {
            setOverlay({ active: false });
            toast.error(formatApiError(error.response?.data?.detail));
        }
    }, [storeData]);

    const cancelPayment = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
        setOverlay({ active: false });
    };

    const getCustomPrice = () => {
        if (!storeData) return 0;
        const ppc = storeData.price_per_credit || 1.0;
        let discount = 0;
        for (const pkg of (storeData.packages || [])) {
            if (customQty >= pkg.credits) discount = Math.max(discount, pkg.discount || 0);
        }
        return Math.round(customQty * ppc * (1 - discount / 100) * 100) / 100;
    };

    const getCustomDiscount = () => {
        if (!storeData) return 0;
        let discount = 0;
        for (const pkg of (storeData.packages || [])) {
            if (customQty >= pkg.credits) discount = Math.max(discount, pkg.discount || 0);
        }
        return discount;
    };

    if (loading) {
        return (
            <DashboardLayout type="operator">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    const packages = storeData?.packages || [];
    const minCredits = storeData?.min_credits || 100;
    const tierIcons = [Zap, Award, TrendingUp];

    return (
        <DashboardLayout type="operator">
            <PaymentOverlay overlay={overlay} onClose={cancelPayment} />

            <div className="space-y-8" data-testid="operator-credits">
                {/* Header */}
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Buy Credits</h1>
                    <p className="text-muted-foreground mt-1">Purchase credits to generate redeem codes for your travelers</p>
                </div>

                {/* Current Balance */}
                <Card className="bg-gradient-to-r from-gold/10 to-gold/5 border-gold/30">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-muted-foreground text-sm mb-1">Current Balance</p>
                                <p className="text-4xl font-bold text-gold" data-testid="credit-balance">{currentCredits}</p>
                                <p className="text-sm text-muted-foreground mt-1">credits available</p>
                            </div>
                            <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center">
                                <Coins className="w-8 h-8 text-gold" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Custom Amount */}
                <Card className="bg-[#121620] border-white/10">
                    <CardHeader>
                        <CardTitle className="text-white font-outfit flex items-center gap-2">
                            <CreditCard className="w-5 h-5 text-gold" />
                            Custom Amount
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-8">
                            <div className="flex-1 space-y-3">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm text-muted-foreground">Credits</label>
                                    <span className="text-sm text-muted-foreground">Min: {minCredits}</span>
                                </div>
                                <Slider
                                    value={[customQty]}
                                    min={minCredits}
                                    max={2000}
                                    step={50}
                                    onValueChange={(v) => setCustomQty(v[0])}
                                    className="w-full"
                                />
                                <div className="flex items-center justify-between">
                                    <input
                                        type="number"
                                        value={customQty}
                                        onChange={(e) => setCustomQty(Math.max(minCredits, parseInt(e.target.value) || minCredits))}
                                        className="w-28 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-center text-lg font-semibold focus:border-gold/50 focus:outline-none"
                                        min={minCredits}
                                        data-testid="custom-credits-input"
                                    />
                                    <div className="text-right">
                                        {getCustomDiscount() > 0 && (
                                            <p className="text-xs text-emerald-400 mb-0.5">{getCustomDiscount()}% discount applied</p>
                                        )}
                                        <p className="text-2xl font-bold text-white">{formatCurrency(getCustomPrice())}</p>
                                        <p className="text-xs text-muted-foreground">{formatCurrency(getCustomPrice() / customQty)}/credit</p>
                                    </div>
                                </div>
                            </div>
                            <Button
                                onClick={() => startPurchase(customQty)}
                                className="bg-gold text-[#0A0D14] hover:bg-gold/90 h-12 px-8 font-semibold shrink-0"
                                data-testid="buy-custom-btn"
                            >
                                <CreditCard className="w-4 h-4 mr-2" />
                                Buy {customQty} Credits
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Packages */}
                <div>
                    <h2 className="font-outfit text-xl font-semibold text-white mb-4">Quick Packages</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {packages.map((pkg, i) => {
                            const TierIcon = tierIcons[i] || Zap;
                            return (
                                <Card
                                    key={pkg.name}
                                    className={`bg-[#121620] border-white/10 hover:border-gold/30 transition-all ${i === 1 ? 'ring-1 ring-gold/30' : ''}`}
                                >
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-white font-outfit flex items-center justify-between">
                                            <span className="flex items-center gap-2">
                                                <TierIcon className="w-4 h-4 text-gold" />
                                                {pkg.name}
                                            </span>
                                            {i === 1 && (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] bg-gold/10 text-gold uppercase tracking-wider">Popular</span>
                                            )}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div>
                                            <p className="text-3xl font-bold text-white">{formatCurrency(pkg.price)}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <p className="text-muted-foreground text-sm">{pkg.credits} credits</p>
                                                {pkg.discount > 0 && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                                                        {pkg.discount}% OFF
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <ul className="space-y-2">
                                            <li className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                                                {formatCurrency(pkg.price_per_credit || (pkg.price / pkg.credits))}/credit
                                            </li>
                                            <li className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                                                Never expires
                                            </li>
                                        </ul>
                                        <Button
                                            onClick={() => startPurchase(pkg.credits)}
                                            className={`w-full ${i === 1 ? 'bg-gold text-[#0A0D14] hover:bg-gold/90' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                                            data-testid={`buy-pkg-${i}-btn`}
                                        >
                                            <CreditCard className="w-4 h-4 mr-2" />
                                            Buy Now
                                        </Button>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>

                {/* Info */}
                <div className="text-center py-4">
                    <p className="text-xs text-white/30 flex items-center justify-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Payments are securely processed via Stripe. Credits are added instantly after payment.
                    </p>
                </div>
            </div>
        </DashboardLayout>
    );
}
